import type { Theme } from './types'

const rawDefaultTheme: unknown = import.meta.env.VITE_DEFAULT_THEME
export const envDefaultTheme: string =
  typeof rawDefaultTheme === 'string' && rawDefaultTheme.trim()
    ? rawDefaultTheme.trim()
    : 'newspaper'

export type { Theme } from './types'

interface ThemeManifest {
  id: string
  name: string
  /** Relative to this manifest's own directory, e.g. "./images/logo.png". */
  logoPath: string
  /** One or more CSS files, relative to this manifest's directory, joined
   *  in order (e.g. the newspaper theme layers editorial.css on colors.css). */
  cssPath: string | string[]
}

// Every theme is declared as `themes/<id>/theme.json` (metadata) plus its own
// `styles/*.css` and `images/logo.*` — dropping a new theme folder in
// registers it automatically, no import/list edit needed. `theme.json` never
// touches the actual CSS/image content itself: that still lives in real
// `.css`/image files, loaded below exactly as an `index.ts` theme would have
// (`?raw` for CSS text, a plain asset import for the logo's build URL) —
// `theme.json` only holds the *paths* to them.
const manifests = import.meta.glob<ThemeManifest>('./*/theme.json', {
  eager: true,
  import: 'default',
})
const cssFiles = import.meta.glob<string>('./*/**/*.css', {
  eager: true,
  query: '?raw',
  import: 'default',
})
const logoFiles = import.meta.glob<string>('./*/**/*.{png,jpg,jpeg,svg}', {
  eager: true,
  import: 'default',
})

// Resolves a manifest-relative path (e.g. "./images/logo.png") against the
// manifest's own glob key (e.g. "./dark/theme.json") into the glob key format
// `cssFiles`/`logoFiles` are indexed by (e.g. "./dark/images/logo.png").
function resolveAssetKey(manifestKey: string, relativePath: string): string {
  const themeDir = manifestKey.replace(/\/theme\.json$/, '')
  return `${themeDir}/${relativePath.replace(/^\.\//, '')}`
}

export const themes: Theme[] = Object.keys(manifests)
  .sort()
  .flatMap((manifestKey) => {
    const manifest = manifests[manifestKey]
    const cssPaths = Array.isArray(manifest.cssPath) ? manifest.cssPath : [manifest.cssPath]
    const cssText = cssPaths.map((path) => cssFiles[resolveAssetKey(manifestKey, path)]).join('\n')
    const logoUrl = logoFiles[resolveAssetKey(manifestKey, manifest.logoPath)]
    return [{ id: manifest.id, name: manifest.name, logoUrl, css: cssText }]
  })

// Deliberately explicit rather than `themes[0]`, so `getThemeById`'s
// fallback for an unrecognized id doesn't silently change if the
// auto-discovered order ever shifts (e.g. a new theme sorts before it).
const FALLBACK_THEME_ID = 'dark'

export function getThemeById(themeId: string): Theme {
  return (
    themes.find((theme) => theme.id === themeId) ??
    themes.find((theme) => theme.id === FALLBACK_THEME_ID) ??
    themes[0]
  )
}
